import React, { useEffect, useState, ReactNode } from 'react'
import { BadgeVariant, Flex, Modal, StatusBadge } from '@mprolab/uikit'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import Image from 'components/CompatImage'
import { tokenLogoSrc } from 'utils/tokenLogoUri'
import { BigNumber } from 'ethers'
import { observer } from 'mobx-react-lite'
import { StakeProgress, stakeStore } from 'mobxStores/StakeStore'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import ChainScanFooter from 'components/ChainScanFooter'
import { useClassicStake } from '../classicStake.logic'

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 550px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const BadgeWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`

interface UnstakeProps {
  fetchClassicStakeConfig: (userAccount?: string, refetch?: boolean) => Promise<void>
  onDismiss?: () => void
}

const UnstakeModalV2: React.FC<UnstakeProps> = observer(({ onDismiss, fetchClassicStakeConfig }) => {
  const { stake, stakeProgress, error, txHash } = stakeStore
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { unstake, getReleaseInfo } = useClassicStake()
  const [blockedReason, setBlockedReason] = useState<string | null>(null)

  const stakeToken = (): { name: string; image: ReactNode } => {
    const tokenAImage = stake?.token0?.logoURI || stake?.stakeToken?.logoURI
    const tokenBImage = stake?.token1?.logoURI

    return {
      name: stake.stakeToken?.token || `${stake.token0?.token}-${stake.token1?.token}`,
      image: (
        <Flex width={tokenBImage ? '32px' : '16'} height="16px" position="relative">
          <Image src={tokenLogoSrc(tokenAImage)} width={16} height={16} />
          {tokenBImage && <Image src={tokenLogoSrc(tokenBImage)} width={16} height={16} />}
        </Flex>
      )
    }
  }

  const { name: stakeTokenName } = stakeToken()

  useEffect(() => {
    ;(async () => {
      try {
        /*
         * Ask before sending. This modal used to fire `claim()` the instant it
         * opened, checking nothing — the classic unstake modal has always read
         * the releasable amount first. On a double-reward stake the release
         * comes in periods, so between them there is nothing to claim and the
         * transaction cannot succeed. Sending it anyway cost the user a
         * rejected wallet prompt and told them "Something went wrong, try
         * again in a while", which is wrong twice: nothing went wrong, and
         * trying again in a while is not what helps.
         */
        const { releasable, nextReleaseAt } = await getReleaseInfo(stake.contractAddress)
        // -1 means the read failed. Unreadable is not zero, and it must never
        // lock somebody out of their own tokens — proceed and let the chain
        // answer.
        if (releasable.isZero()) {
          setBlockedReason(
            nextReleaseAt
              ? t('stake.nothingToReleaseUntil', {
                  date: new Date(nextReleaseAt * 1000).toLocaleString()
                })
              : t('stake.nothingToRelease')
          )
          stakeStore.setStakeProgress(StakeProgress.ERROR)
          return
        }

        stakeStore.setStakeProgress(StakeProgress.CONFIRMING)
        await unstake(stake.contractAddress, BigNumber.from(0))
        await fetchClassicStakeConfig(account, true)
      } catch (error) {
        stakeStore.setStakeProgress(StakeProgress.ERROR)
      }
    })()
  }, [])

  const unstakeModalContent = () => {
    switch (stakeProgress) {
      case StakeProgress.APPROVING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.transactionApproving')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.CONFIRMING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.continueInWallet')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.PENDING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('transactionPending')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.SUCCESS:
        return (
          <Flex flexDirection="column" gap="16px">
            <BadgeWrapper>
              <StatusBadge title={t('modal.transactionSucessful')} variant={BadgeVariant.SUCCESS} />
            </BadgeWrapper>
            <ChainScanFooter chainId={stake?.chainId} transactionHash={txHash} />
          </Flex>
        )
      case StakeProgress.ERROR:
      case StakeProgress.REJECTED:
        return (
          <BadgeWrapper>
            <StatusBadge title={blockedReason ?? getErrorCodesDescription(t, error)} variant={BadgeVariant.ERROR} />
          </BadgeWrapper>
        )

      default:
        return <></>
    }
  }

  const modalTitle = () => {
    switch (stakeProgress) {
      case StakeProgress.SUCCESS:
        return `${t('stakingPanel.unstake')} ${stakeTokenName}`
      case StakeProgress.CONFIRMING:
        return t('modal.confirmTransaction')
      case StakeProgress.APPROVING:
        return t('modal.approving')
      case StakeProgress.PENDING:
        return t('modal.transactionConfirmation')
      case StakeProgress.ERROR:
        return t('modal.transactionFailed')
      case StakeProgress.REJECTED:
        return t('modal.rejected')
      default:
        return ''
    }
  }

  return (
    <Modal onDismiss={onDismiss} hideCloseButton={false} title={modalTitle()}>
      <ContentWrapper>{unstakeModalContent()}</ContentWrapper>
    </Modal>
  )
})

export default UnstakeModalV2
